import { Filter, Search, X, ChevronRight } from "lucide-react";
import { COLORS } from "../styles/tokens";
import { useLockBodyScroll, useSwipeToDismiss } from "../hooks";
import type { SignalPackage } from "../types/realsynq";
import { ACTIVATION_PATHS, TTL_BUCKETS, PACKAGES } from "../data/packages";

interface FilterBarProps {
  search: string;
  setSearch: (v: string) => void;
  vertical: string;
  setVertical: (v: string) => void;
  path: string;
  setPath: (v: string) => void;
  ttl: string;
  setTtl: (v: string) => void;
  allVerticals: string[];
  filtered: SignalPackage[];
  clearFilters: () => void;
  activeFilterCount: number;
  isMobile: boolean;
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
}

export function FilterBar(props: FilterBarProps) {
  const {
    search, setSearch, vertical, setVertical, path, setPath, ttl, setTtl,
    allVerticals, filtered, clearFilters, activeFilterCount,
    isMobile, filtersOpen, setFiltersOpen,
  } = props;

  if (isMobile) {
    return (
      <>
        <section
          className="glass"
          style={{
            padding: "16px",
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={14}
              color={COLORS.inkMute}
              style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }}
            />
            <input
              placeholder="Search signals, packages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              enterKeyHint="search"
              style={{ padding: search ? "12px 44px 12px 38px" : "12px 12px 12px 38px", borderRadius: 8, fontSize: 14, width: "100%" }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                aria-label="Clear search"
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "transparent",
                  border: "none",
                  color: COLORS.inkMute,
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </section>

        {filtersOpen && (
          <MobileFilterSheet
            vertical={vertical} setVertical={setVertical}
            path={path} setPath={setPath}
            ttl={ttl} setTtl={setTtl}
            allVerticals={allVerticals}
            clearFilters={clearFilters}
            activeFilterCount={activeFilterCount}
            onClose={() => setFiltersOpen(false)}
            resultCount={filtered.length}
          />
        )}
      </>
    );
  }

  return (
    <section
      className="glass"
      style={{
        padding: "32px",
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Filter size={14} color={COLORS.inkDim} />
          <span className="mono" style={{ fontSize: 11, color: COLORS.inkDim, letterSpacing: "0.1em" }}>
            REFINE
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", gap: 12 }}>
          <div style={{ position: "relative" }}>
            <Search
              size={14}
              color={COLORS.inkMute}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
            />
            <input
              placeholder="Search signals, packages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: "10px 12px 10px 36px", borderRadius: 6, fontSize: 13, width: "100%" }}
            />
          </div>
          <FilterSelect label="Vertical" value={vertical} onChange={setVertical} options={allVerticals} />
          <FilterSelect label="Activation" value={path} onChange={setPath} options={["All", ...ACTIVATION_PATHS]} />
          <FilterSelect label="TTL" value={ttl} onChange={setTtl} options={["All", ...TTL_BUCKETS]} />
        </div>
        {activeFilterCount > 0 && (
          <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12 }}>
            <span className="mono" style={{ fontSize: 11, color: COLORS.cyan }}>
              {filtered.length} OF {PACKAGES.length} PACKAGES
            </span>
            <button
              onClick={clearFilters}
              style={{
                background: "transparent",
                border: "none",
                color: COLORS.inkDim,
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              Clear <X size={12} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

interface MobileFilterSheetProps {
  vertical: string;
  setVertical: (v: string) => void;
  path: string;
  setPath: (v: string) => void;
  ttl: string;
  setTtl: (v: string) => void;
  allVerticals: string[];
  clearFilters: () => void;
  activeFilterCount: number;
  onClose: () => void;
  resultCount: number;
}

function MobileFilterSheet({
  vertical, setVertical, path, setPath, ttl, setTtl,
  allVerticals, clearFilters, activeFilterCount, onClose, resultCount,
}: MobileFilterSheetProps) {
  useLockBodyScroll(true);
  const { dragY, handlers } = useSwipeToDismiss(onClose);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 90,
        background: "rgba(5, 9, 15, 0.7)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="slide-up"
        style={{
          width: "100%",
          maxHeight: "85vh",
          background: COLORS.bgCardSolid,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          borderTop: `1px solid ${COLORS.borderHi}`,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: dragY ? "none" : "transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <div
          {...handlers}
          onClick={onClose}
          role="button"
          aria-label="Close — drag down or tap"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 28,
            padding: "12px 0 8px",
            cursor: "grab",
            touchAction: "none",
          }}
        >
          <div style={{ width: 40, height: 4, borderRadius: 2, background: COLORS.borderHi }} />
        </div>

        <div
          style={{
            padding: "12px 20px 16px",
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="display" style={{ fontSize: 20, fontWeight: 700 }}>
            Filters
            {activeFilterCount > 0 && (
              <span
                className="mono"
                style={{
                  marginLeft: 10,
                  fontSize: 12,
                  color: COLORS.cyan,
                  padding: "3px 8px",
                  borderRadius: 999,
                  background: `${COLORS.cyan}15`,
                  border: `1px solid ${COLORS.cyan}33`,
                  fontWeight: 600,
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </div>
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              style={{
                background: "transparent",
                border: "none",
                color: COLORS.inkDim,
                fontSize: 13,
              }}
            >
              Clear all
            </button>
          )}
        </div>

        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
          <FilterGroup label="Vertical" value={vertical} onChange={setVertical} options={allVerticals} />
          <FilterGroup label="Activation Path" value={path} onChange={setPath} options={["All", ...ACTIVATION_PATHS]} />
          <FilterGroup label="TTL Range" value={ttl} onChange={setTtl} options={["All", ...TTL_BUCKETS]} />
        </div>

        <div
          style={{
            padding: 16,
            borderTop: `1px solid ${COLORS.border}`,
            background: "rgba(5, 9, 15, 0.5)",
          }}
        >
          <button
            onClick={onClose}
            style={{
              width: "100%",
              background: COLORS.blue,
              color: "#ffffff",
              border: "none",
              padding: "14px",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            Show {resultCount} {resultCount === 1 ? "package" : "packages"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FilterGroupProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}

function FilterGroup({ label, value, onChange, options }: FilterGroupProps) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div className="mono" style={{ fontSize: 10, color: COLORS.inkMute, letterSpacing: "0.15em", marginBottom: 12 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map(o => {
          const sel = value === o;
          return (
            <button
              key={o}
              onClick={() => onChange(o)}
              style={{
                padding: "8px 14px",
                borderRadius: 999,
                border: `1px solid ${sel ? COLORS.cyan : COLORS.border}`,
                background: sel ? `${COLORS.cyan}10` : "rgba(10, 19, 32, 0.6)",
                color: sel ? COLORS.cyan : COLORS.inkDim,
                fontSize: 13,
                fontWeight: sel ? 600 : 400,
              }}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}

function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
  return (
    <div style={{ position: "relative" }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 6,
          fontSize: 13,
          paddingRight: 32,
        }}
      >
        {options.map(o => (
          <option key={o} value={o} style={{ background: COLORS.bgCardSolid }}>
            {label}: {o}
          </option>
        ))}
      </select>
      <ChevronRight
        size={14}
        color={COLORS.inkMute}
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%) rotate(90deg)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
